'use strict';

define('vb/private/stateManagement/pageExtension',[
  'vb/private/stateManagement/containerExtension',
  'vb/private/stateManagement/context/pageExtensionContext',
  'vb/private/stateManagement/pageModuleViewModel',
  'vb/private/constants',
  'vb/helpers/mixin',
  'vb/private/stateManagement/fragmentHolderExtensionMixin',
  'vb/private/translations/bundleUtils',
], (ContainerExtension, PageExtensionContext, PageModuleViewModel,
  Constants, Mixin, FragmentHolderExtensionMixin, BundleUtils) => {
  /**
   * PageExtension class
   */
  class PageExtension extends Mixin(ContainerExtension).with(FragmentHolderExtensionMixin) {
    constructor(extension, path, base, className = 'PageExtension') {
      // parent of the pageExtension is the potential extension of the base page parent flow
      const parentContainer = base.getParentFlow();
      let parentExtContainer = null;
      if (parentContainer) {
        parentExtContainer = parentContainer.getParentExtensionContainer(extension);
      }

      super(extension, parentExtContainer, path, base, className);
    }

    static get extensionClass() {
      return PageExtension;
    }

    /**
     *
     * @type {String}
     */
    static get resourceSuffix() {
      return '-page-x.json';
    }

    /**
     * The default event prefix is the lowercase class name (see container.js) but for
     * page extension we want to use the same event prefix as page
     *
     * @type {String}
     */
    // eslint-disable-next-line class-methods-use-this
    get eventPrefix() {
      return 'page';
    }

    get package() {
      return this.base.package;
    }

    // oj-module lifecycle callback
    disconnected() {
      // // record a page deactivated state change
      // StateMonitor.recordStateChange(StateMonitor.RuntimeState.PAGE_DEACTIVATED);
      // StateMonitor.recordStateChange(StateMonitor.RuntimeState.CONTAINER_DEACTIVATED, this);
      // this.deactivated = true;
      this.dispose();
    }

    dispose() {
      delete this.definition;

      super.dispose();
    }

    /**
     * The name of the resource to be used to load the page extension.
     * @type {string}
     */
    get fullName() {
      return `${this.id}-page`;
    }

    /**
     * The name of the runtime environment function to be used to load the descriptor
     *
     * @type {String} the descriptor loader function name
     */
    static get descriptorLoaderName() {
      return 'getPageExtensionDescriptor';
    }

    /**
     * The name of the runtime environment function to be used to load the module functions.
     *
     * @type {String} the module loader function name
     */
    static get functionsLoaderName() {
      return 'getPageExtensionFunctions';
    }

    /**
     * The name of the runtime environment function to be used to load the rules descriptor for the page extension
     *
     * @type {String} the rules descriptor loader function name
     */
    static get rulesDescriptorLoaderName() {
      return 'getPageExtensionRulesDescriptor';
    }

    /**
     * The name of the chain folder is the page name with '-page-x-chains' appended.
     * @type {string}
     */
    get chainsFolderName() {
      return `${this.id}-page-x-chains`;
    }

    /**
     * Returns the ContainerExtensionContext constructor used to create the '$' expression context.
     * @type {typeof PageExtensionContext}
     */
    static get ContextType() {
      return PageExtensionContext;
    }

    /**
     * This is called by ConfigurableMetadataProviderHelper._createExtensionModels when there is
     * dynamic container in the PageExtension. The expectation not to return a page viewModel object
     * but the availableContext.
     * Also see getViewModel on layoutExtension which is called in similar situation but for a
     * dynamic layout extension.
     *
     * @returns {Promise<Object>}
     */
    getViewModel() {
      if (!this.viewModelPromise) {
        this.viewModelPromise = BundleUtils.whenBundlesReady()
          .then(() => new PageModuleViewModel(this));
      }

      return this.viewModelPromise;
    }

    /**
     * Returns a scope resolver map where keys are scope name ("page", "flow" or "application")
     * and value the matching objects. This is used to build the scopeResolver object.
     *
     * @return {Object} an object which properties are scope
     */
    getScopeResolverMap() {
      const map = {
        [Constants.PAGE_PREFIX]: this,
      };

      // if the parent of the pageExtension is defined we need to merge scope resolvers
      // so that the pageExtension has access to its parent's scopes
      if (this.parent) {
        return Object.assign(map, this.parent.getScopeResolverMap());
      }

      return map;
    }

    /**
     * The navPath of a page extension is the navPath of the base page
     *
     * @return {String}  The navigation path.
     */
    calculateNavPath() {
      return this.base.calculateNavPath();
    }

    /**
     * When navigating from a page extension, the only valid destination are page marked
     * with 'fromExternal', so return true so that the check is made.
     *
     * @return {boolean}  true if the navigable flag need to be checked
     */
    // eslint-disable-next-line class-methods-use-this
    isNavigationRestrictedToPublicPage() {
      return true;
    }

    /**
     * To support external navigation in App UI, delegate to the base object
     */
    navigateOperation(options, navContext) {
      return this.base.navigateOperation(options, navContext);
    }
  }

  return PageExtension;
});

